import React, {
  useRef,
  useEffect,
  useCallback,
  useState,
} from 'react';
import { graphql } from 'gatsby';
import gsap from 'gsap';
import isEqual from 'lodash/isEqual';
import { PageHero, Wrapper } from '../components/common';
import { GuidelineNavigation } from '../components/Guidelines';
import { SliceZone } from '../components';
import { toKebabCase } from '../utils/helpers';
import './guideline.scss';

const TYPE_SECTION_ANCHOR = 'section_anchor';

const animateInValues = {
  ease: 'expo.out', y: 0, opacity: 1, duration: 1,
};
const animateOutValues = {
  ease: 'expo.out', y: 20, opacity: 0, duration: 0,
};

const Guideline = (props) => {
  const {
    data: {
      page: {
        data: pageData,
      },
    },
    location,
  } = props;
  const {
    pageName,
    body,
  } = pageData;

  const [sectionsInView, setSectionInView] = useState([]);
  const [reveal, setReveal] = useState(false);
  const bodyRef = useRef();
  const navRef = useRef();
  const timelineRef = useRef();

  const activeSection = sectionsInView.length > 0 ? sectionsInView.reduce((a, b) => Math.min(a, b)) : 0;

  const sectionInViewHandler = (sectionIndex, isInView) => {
    let newSectionsInView = [...sectionsInView];
    const indexExists = newSectionsInView.includes(sectionIndex);
    if (isInView) {
      // Intersection Observer has notified us section has come into view
      // indexExists prevents us from adding duplicates
      if (!indexExists) {
        newSectionsInView = [
          ...sectionsInView,
          sectionIndex,
        ];
      }
    } else {
      // Intersection Observer has notified us section is now out of view
      newSectionsInView = sectionsInView.filter(index => index !== sectionIndex);
    }
    if (!isEqual(sectionsInView, newSectionsInView)) {
      // Only if a new section has come into, or an existing section has come
      // out of view, we update the state
      setSectionInView(newSectionsInView);
    }
  };

  const getNavSections = useCallback((slices) => {
    const navigationSections = [];
    let currentIndex = -1;
    const lastIndex = slices.length - 1;
    for (let i = 0; i <= lastIndex; i += 1) {
      if (slices[i].sliceType === TYPE_SECTION_ANCHOR) {
        if (i > 0 && currentIndex > -1) navigationSections[currentIndex].endIndex = i - 1;
        navigationSections.push({
          title: slices[i].primary.title.text || null,
          id: toKebabCase(slices[i].primary.title.text || null),
          startIndex: i,
          endIndex: null,
          children: [],
        });
        currentIndex += 1;
      } else if (currentIndex > -1) {
        if (slices[i].primary.title.text) {
          navigationSections[currentIndex].children.push({
            title: slices[i].primary.title.text,
            id: toKebabCase(slices[i].primary.title.text),
          });
        }
      }
      if (i === lastIndex) {
        navigationSections[currentIndex].endIndex = i;
      }
    }
    return navigationSections;
  }, []);

  const navSections = getNavSections(body);

  useEffect(() => {
    timelineRef.current = gsap.timeline();
    timelineRef.current.to(navRef.current, { ...animateInValues }, 0);
    timelineRef.current.to(bodyRef.current, { ...animateInValues, delay: 0.2 }, 0);
    gsap.set(navRef.current, animateOutValues);
    gsap.set(bodyRef.current, animateOutValues);
  }, []);

  useEffect(() => {
    if (timelineRef.current) {
      setTimeout(() => setReveal(true), 1200);
    }
  }, []);

  useEffect(() => {
    if (timelineRef.current) {
      if (reveal) {
        timelineRef.current.play();
      } else {
        timelineRef.current.pause(0);
      }
    }
  }, [reveal]);

  return (
    <>
      <div className="guideline">
        <PageHero
          title={pageName}
        />
        <Wrapper>
          <div className="cols">
            <div className="col col-left" ref={navRef}>
              <GuidelineNavigation
                navigation={navSections}
                activeSection={activeSection}
              />
            </div>
            <div className="col col-right" ref={bodyRef}>
              <SliceZone
                allSlices={body}
                location={location}
                sectionInViewHandler={sectionInViewHandler}
              />
            </div>
          </div>
        </Wrapper>
      </div>
    </>
  );
};

export default Guideline;

export const pageQuery = graphql`
  query GuidelineQuery($uid: String!) {
    page: prismicGuideline(uid: { eq: $uid }) {
      uid,
      data {
        pageName: page_name {
          text
        }
        metaTitle: meta_title {
          html
          text
        },
        metaDescription: meta_description {
          html
          text
        },
        openGraphImage: open_graph_image {
          alt
          copyright
          url
        }
        body {
          ... on PrismicGuidelineBodySectionAnchor {
            id
            sliceType: slice_type
            primary {
              title {
                text
              }
            }
          }
          ... on PrismicGuidelineBodyContactFormSlice {
            id
            sliceType: slice_type
            primary {
              title {
                text
              }
              textBody: text_body {
                html
              }
            }
          }
          ... on PrismicGuidelineBodyTextImageSlice {
            id
            sliceType: slice_type
            primary {
              title {
                text
              }
              lastUpdated: last_updated(formatString: "D.M.YYYY")
              textBody: text_body {
                html
              }
              linkText: link_text {
                text
              }
              imageOrientation: image_orientation
              image {
                fluid(maxWidth: 1400, maxHeight: 800) {
                  ...GatsbyPrismicImageFluid_noBase64
                }
                url
                alt
              }
            }
            items {
              link {
                url
                type
                slug
              }
              linkLabel: link_label {
                text
              }
            }
          }
          ... on PrismicGuidelineBodyTextVideoSlice {
            id
            sliceType: slice_type
            primary {
              title {
                text
              }
              lastUpdated: last_updated
              textBody: text_body {
                html
              }
              videoProvider: video_provider
              videoUrl: video_url {
                url
              }
            }
          }
          ... on PrismicGuidelineBodyGallerySlice {
              id
              sliceType: slice_type
              primary {
                title {
                  text
                }
              }
              items {
                image {
                  fluid(maxWidth: 1400, maxHeight: 800) {
                    ...GatsbyPrismicImageFluid_noBase64
                  }
                  url
                  alt
                }
                caption {
                  text
                }
              }
            }
            ... on PrismicGuidelineBodyTextSlice {
              id
              sliceType: slice_type
              primary {
                title {
                  text
                }
                lastUpdated: last_updated(formatString: "D.M.YYYY")
                textBody: text_body {
                  html
                }
              }
              items {
                link {
                  url
                  type
                  slug
                }
                linkLabel: link_label {
                  text
                }
              }
            }
            ... on PrismicGuidelineBodyArticlesSlice {
              id
              sliceType: slice_type
              primary {
                title {
                  text
                }
                lastUpdated: last_updated(formatString: "D.M.YYYY")
                textBody: text_body {
                  html
                }
              }
              items {
                article {
                  document {
                    ... on PrismicArticle {
                      id
                      uid
                      data {
                        featuredImage: featured_image {
                          fluid(maxWidth: 1400, maxHeight: 800) {
                            ...GatsbyPrismicImageFluid_noBase64
                          }
                          url
                          alt
                        }
                        postTitle: post_title {
                          text
                        }
                        previewSnippet: preview_snippet {
                          text
                        }
                      }
                    }
                  }
                }
              }
            }
            ... on PrismicGuidelineBodyResourcesSlice {
              id
              sliceType: slice_type
              primary {
                title {
                  text
                }
                lastUpdated: last_updated(formatString: "D.M.YYYY")
                textBody: text_body {
                  html
                }
              }
              items {
                title {
                  text
                }
                image {
                  fluid(maxWidth: 1400, maxHeight: 800) {
                    ...GatsbyPrismicImageFluid_noBase64
                  }
                  url
                  alt
                }
                description {
                  text
                }
                asset {
                  url
                  slug
                  type
                }
              }
            }
            ... on PrismicGuidelineBodyChannelResourcesSlice {
              id
              sliceType: slice_type
              primary {
                title {
                  text
                }
                lastUpdated: last_updated(formatString: "D.M.YYYY")
                textBody: text_body {
                  html
                }
              }
              items {
                title {
                  text
                }
                image {
                  fluid(maxWidth: 800, maxHeight: 800) {
                    ...GatsbyPrismicImageFluid_noBase64
                  }
                  url
                  alt
                }
                description {
                  text
                }
                asset {
                  url
                  slug
                  type
                }
                type
              }
            }
        }
      }
    }
  }
`;
